<template>
  <component :is="tag" v-if="advertisement.isAdvertised">
    <slot name="prefix" />

    <slot name="default" v-bind="advertisement">
      <PaymentMethodsErrors
        :class="[classes.typography, classes.color]"
        :inner-class="`flex items-center ${classes.gap}`"
        :payment-methods="[advertisement.method]"
      >
        <PaymentIcons
          v-if="hasIcon"
          :class="['self-start', classes.saturate, iconClasses]"
          :methods="[advertisement.method]"
          show-disabled-methods
        />

        <div class="grow">
          {{ advertisementDetails.plainText }}

          <component
            :is="modalOrComponent"
            v-if="modalOrComponent"
            :base-price
            class="inline"
            :payment-methods
            :spot="advertisement.spot"
            @open="openAndEmitEvent"
          >
            <template
              #trigger="{ open, ariaLabel }"
              v-if="advertisementDetails.linkText"
            >
              <RevLink :aria-label @click="openAndEmitEvent(open)">
                {{ advertisementDetails.linkText }}
              </RevLink>
            </template>
          </component>
        </div>
      </PaymentMethodsErrors>
    </slot>
    <slot name="suffix" />
  </component>

  <component :is="tag" v-else-if="$slots.fallback">
    <slot name="fallback" />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { RevLink } from '@ds/components/Link'

import PaymentIcons from '../../../methods/components/PaymentIcons/PaymentIcons.vue'
import PaymentMethodsErrors from '../../../methods/components/PaymentMethodsErrors/PaymentMethodsErrors.vue'
import { usePaymentMethodsWithFallback } from '../../../methods/composables/usePaymentMethodsWithFallback'
import { isPaymentMethodEnabled } from '../../../methods/helpers/methods'
import { usePaymentAdvertisement } from '../../composables/usePaymentAdvertisement'
import type {
  CartPaymentAdvertisingConfig,
  OrdersPaymentAdvertisingConfig,
  ProductPaymentAdvertisingConfig,
} from '../../types/PaymentAdvertisingConfig'
import { PaymentAdvertisingSpot } from '../../types/PaymentAdvertisingSpot'

import translations from './PaymentAdvertisement.translations'

const i18n = useI18n()

const emit = defineEmits(['open'])

const props = withDefaults(
  defineProps<{
    basePrice: Price
    iconClasses?: string
    spot: PaymentAdvertisingSpot
    paymentMethods?: PaymentMethod[] | null
    tag?: string
    textSize?: '1' | '2'
  }>(),
  {
    iconClasses: '',
    paymentMethods: null,
    tag: 'div',
    textSize: '2',
  },
)

const paymentMethods = usePaymentMethodsWithFallback(() => props.paymentMethods)

const includeDisabledMethods = computed(
  () => props.spot === PaymentAdvertisingSpot.CART,
)
const advertisement = usePaymentAdvertisement(
  () => props.spot,
  paymentMethods,
  { includeDisabledMethods },
)

const isAvailable = computed(
  () =>
    advertisement.value.isAdvertised &&
    isPaymentMethodEnabled(advertisement.value.method),
)

const modalOrComponent = computed(() => {
  if (!advertisement.value.isAdvertised || !isAvailable.value) {
    return null
  }

  return advertisement.value.config.isHandledByThirdParty
    ? advertisement.value.config.component
    : advertisement.value.config.modal
})

const advertisementDetails = computed(() => {
  if (!advertisement.value.isAdvertised) {
    return {}
  }

  if (props.spot === PaymentAdvertisingSpot.CART) {
    const config = advertisement.value.config as CartPaymentAdvertisingConfig

    if (!isAvailable.value) {
      return { plainText: i18n(config.unavailable) }
    }

    return config.isHandledByThirdParty
      ? {}
      : { linkText: i18n(config.available) }
  }

  const config = advertisement.value.config as
    | OrdersPaymentAdvertisingConfig
    | ProductPaymentAdvertisingConfig

  return config.isHandledByThirdParty
    ? {}
    : {
        linkText: i18n(translations.learnMore),
        plainText: i18n(config.text),
      }
})

const hasIcon = computed(
  () =>
    Boolean(advertisementDetails.value.linkText) ||
    Boolean(advertisementDetails.value.plainText),
)

const classesByTextSize = {
  1: {
    typography: tw`body-1`,
    gap: tw`gap-x-8`,
  },
  2: {
    typography: tw`body-2`,
    gap: tw`gap-x-4`,
  },
}

const classesByAvailability = {
  true: {
    color: tw`text-primary`,
    saturate: '',
  },
  false: {
    color: tw`tw-text-primary-disabled`,
    saturate: tw`saturate-0`,
  },
}

const classes = computed(() => ({
  ...classesByAvailability[`${isAvailable.value}`],
  ...classesByTextSize[props.textSize],
}))

function openAndEmitEvent(open?: () => void) {
  if (open) {
    open()
  }
  emit('open', advertisement)
}
</script>
